import { Fragment, useState, useMemo, useEffect, forwardRef } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import styled from '@emotion/styled';
import { keyframes, useTheme } from '@emotion/react';

import { FieldLabel } from '../FieldLabel';
import { Message } from '../Message';

import type { SelectProps, SelectOption, FontSizeValue } from './Select.types';
import type { Theme } from '../../theme';

/**
 * Map theme font size token to actual value
 */
const getFontSizeValue = (fontSize: FontSizeValue | undefined, theme: Theme): string | undefined => {
  if (!fontSize) {
    return undefined;
  }
  
  // Check if it's a theme token key
  const themeTokens = ['xs', 'sm', 'base', 'md', 'lg', 'xl'] as const;
  if (themeTokens.includes(fontSize as typeof themeTokens[number])) {
    return theme.typography.fontSize[fontSize as keyof typeof theme.typography.fontSize];
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

/**
 * Styled Components - Production styles from css/style.css lines 969-1143
 */

const Container = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  min-width: ${props => props.$fullWidth ? 'auto' : '180px'};
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: visible;
`;

const Button = styled.button<{
  $size: 'sm' | 'md';
  $variant: 'default' | 'filled' | 'outlined';
  $error: boolean;
  $isOpen: boolean;
  $hasValue: boolean;
  $focusColor?: string;
}>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  border-radius: ${props => props.theme.borders.radius.sm};
  font-family: ${props => props.theme.typography.fontFamily};
  font-weight: ${props => props.theme.typography.fontWeight.regular};
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  color: ${props => props.$hasValue ? props.theme.colors.text.primary : props.theme.colors.text.muted};
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.border.input};
  
  /* Size variants (sm: 32px, md: 36px) */
  ${props => props.$size === 'sm' && `
    height: 32px;
    padding: 4px 8px;
    font-size: ${props.theme.typography.fontSize.xs};
  `}
  
  ${props => props.$size === 'md' && `
    height: 36px;
    padding: 5px 6px 5px 12px;
    font-size: ${props.theme.typography.fontSize.sm};
  `}
  
  /* Hover state */
  &:hover:not(:disabled) {
    border-color: ${props => props.$focusColor || props.theme.colors.text.primary};
    color: ${props => props.theme.colors.text.primary};
  }
  
  /* Active/Open state */
  ${props => props.$isOpen && `
    border-color: ${props.$focusColor || props.theme.colors.text.primary};
    color: ${props.theme.colors.text.primary};
    ${props.$focusColor ? `box-shadow: 0 0 0 2px ${props.$focusColor}20;` : ''}
  `}
  
  /* Error state */
  ${props => props.$error && `
    border-color: ${props.theme.colors.status.error};
  `}
  
  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
  
  /* Focus state */
  &:focus {
    outline: none;
    ${props => props.$focusColor && !props.$error ? `
      border-color: ${props.$focusColor};
      box-shadow: 0 0 0 2px ${props.$focusColor}20;
    ` : ''}
  }
  
  &:focus-visible {
    outline: 2px solid ${props => props.$focusColor || `var(--colour-highlight, var(--color-highlight, ${props.theme.colors.highlight}))`};
    outline-offset: 2px;
  }
`;

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ButtonText = styled.span<{ $fontSize?: string }>`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  ${props => props.$fontSize && `font-size: ${props.$fontSize};`}
`;

const ButtonActions = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${props => props.theme.colors.text.muted};
  border-radius: ${props => props.theme.borders.radius.sm};
  transition: color 0.15s ease-out, background-color 0.15s ease-out;
  
  &:hover {
    color: ${props => props.theme.colors.text.primary};
    background-color: ${props => props.theme.colors.background.hover};
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid var(--colour-highlight, var(--color-highlight, ${props => props.theme.colors.highlight}));
    outline-offset: 1px;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingSpinner = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: ${spin} 0.8s linear infinite;
  color: ${props => props.theme.colors.text.muted};
`;

const Chevron = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.theme.colors.text.muted};
  transition: transform 0.2s ease-out;
  margin-left: 4px;
  
  ${props => props.$isOpen && `
    transform: rotate(180deg);
  `}
`;

const Icon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.theme.colors.text.secondary};
`;

const Options = styled.ul<{ $menuPlacement: 'auto' | 'bottom' | 'top' }>`
  position: absolute;
  z-index: ${props => props.theme.zIndex.dropdown};
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
  max-height: 300px;
  overflow: auto;
  border-radius: ${props => props.theme.borders.radius.sm};
  background-color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.dropdown};
  outline: none;
  color: ${props => props.theme.colors.text.primary};
  
  /* Menu placement */
  ${props => props.$menuPlacement === 'top' ? `
    bottom: 100%;
    margin-bottom: 4px;
  ` : `
    top: 100%;
    margin-top: 4px;
  `}
  
  /* Transition classes for Headless UI */
  &.leave {
    transition: opacity 200ms ease-out;
  }
  
  &.leave-from {
    opacity: 1;
  }
  
  &.leave-to {
    opacity: 0;
  }
`;

const SearchWrapper = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`;

const SearchInput = styled.input<{ $fontSize?: string }>`
  width: 100%;
  padding: 8px;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.$fontSize || props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.white};
  outline: none;
  box-sizing: border-box;
  
  &:focus {
    border-color: ${props => props.theme.colors.text.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
  }
`;

const OptionsList = styled.div`
  padding: 0;
`;

const OptionItem = styled.li<{ $active: boolean; $selected: boolean; $fontSize?: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 12px;
  height: 36px;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.$fontSize || props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  white-space: nowrap;
  
  /* First child border radius */
  &:first-of-type {
    border-top-left-radius: ${props => props.theme.borders.radius.sm};
    border-top-right-radius: ${props => props.theme.borders.radius.sm};
  }
  
  /* Last child border radius */
  &:last-of-type {
    border-bottom-left-radius: ${props => props.theme.borders.radius.sm};
    border-bottom-right-radius: ${props => props.theme.borders.radius.sm};
  }
  
  /* Hover/Active state - uses highlight color */
  ${props => props.$active && `
    background-color: var(--colour-highlight, var(--color-highlight, ${props.theme.colors.highlight}));
    color: var(--colour-highlight-text, var(--color-highlight-text, ${props.theme.colors.white}));
  `}
  
  /* Selected state - uses highlight color directly (no opacity) */
  ${props => props.$selected && !props.$active && `
    background-color: var(--colour-highlight, var(--color-highlight, ${props.theme.colors.highlight}));
    color: var(--colour-highlight-text, var(--color-highlight-text, ${props.theme.colors.white}));
    opacity: 0.85;
  `}
  
  /* Disabled state */
  &[aria-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const OptionContent = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const OptionLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  padding: 0;
  height: auto;
`;

const Checkmark = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--colour-highlight-text, var(--color-highlight-text, ${props => props.theme.colors.white}));
`;

const NoResults = styled.div<{ $fontSize?: string }>`
  padding: 12px;
  text-align: center;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.$fontSize || props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.muted};
`;

// Hidden input for form submission
const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Select component - Accessible dropdown select using Headless UI
 *
 * @example
 * ```tsx
 * const options = [
 *   { value: '1', label: 'Option 1' },
 *   { value: '2', label: 'Option 2' },
 *   { value: '3', label: 'Option 3', disabled: true }
 * ];
 *
 * <Select
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 *   options={options}
 *   placeholder="Select an option"
 *   isClearable
 * />
 * ```
 */
export const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(
  {
    value,
    onChange,
    options,
    label,
    placeholder = 'Select...',
    size = 'md',
    variant = 'default',
    error = false,
    errorMessage,
    disabled = false,
    required = false,
    fullWidth = false,
    searchable = false,
    className,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby,
    'aria-labelledby': ariaLabelledby,
    'data-testid': testId,
    dataTestId,
    dataId,
    isSearchable,
    clearable,
    isClearable = true, // Default to true - show clear button for optional fields
    isLoading = false,
    assistiveText,
    onMenuOpen,
    onMenuClose,
    onInputChange,
    selectRef,
    name,
    menuPlacement = 'bottom',
    noResultsMessage = 'No options found',
    searchPlaceholder = 'Search...',
    // Font size customization props
    labelFontSize,
    selectedFontSize,
    optionFontSize,
    focusColor,
  },
  ref
) {
  const [query, setQuery] = useState('');
  const [wasOpen, setWasOpen] = useState(false);
  const theme = useTheme() as Theme;

  // Support both searchable and isSearchable props
  const isSearchEnabled = searchable || isSearchable;

  // Support both clearable and isClearable props
  // Clear button should not show when field is required
  const isClearEnabled = (clearable || isClearable) && !required;

  // Compute size-based default font size (sm: xs/12px, md: sm/14px)
  const defaultFontSize = size === 'sm' ? theme.typography.fontSize.xs : theme.typography.fontSize.sm;
  
  // Compute resolved font sizes with size-based defaults
  const resolvedLabelFontSize = getFontSizeValue(labelFontSize, theme) || defaultFontSize;
  const resolvedSelectedFontSize = getFontSizeValue(selectedFontSize, theme) || defaultFontSize;
  const resolvedOptionFontSize = getFontSizeValue(optionFontSize, theme) || defaultFontSize;

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    if (!isSearchEnabled || !query) {
      return options;
    }
    const lowerQuery = query.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerQuery)
    );
  }, [options, query, isSearchEnabled]);

  const handleChange = (newValue: string) => {
    onChange(newValue);
    setQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
    setQuery('');
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    onInputChange?.(newQuery);
  };

  // Handle menu open/close callbacks
  const handleOpenChange = (open: boolean) => {
    if (open && !wasOpen) {
      onMenuOpen?.();
    } else if (!open && wasOpen) {
      onMenuClose?.();
    }
    setWasOpen(open);
  };

  // Merge refs
  useEffect(() => {
    if (selectRef && typeof selectRef === 'object') {
      // This is handled by the container ref
    }
  }, [selectRef]);

  return (
    <Container
      $fullWidth={fullWidth}
      className={className}
      data-testid={dataTestId || testId}
      data-id={dataId}
      ref={(node) => {
        // Forward ref
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        // Also set selectRef if provided
        if (selectRef && typeof selectRef === 'object') {
          (selectRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }}
    >
      {label && (
        <FieldLabel
          label={label}
          htmlFor={`${testId}-trigger`}
          required={required}
          id={testId ? `${testId}-label` : undefined}
          fontSize={resolvedLabelFontSize}
        />
      )}
      <Listbox value={value || ''} onChange={handleChange} disabled={disabled}>
        {({ open }) => {
          // Track open state changes
          if (open !== wasOpen) {
            handleOpenChange(open);
          }

          return (
            <>
              {/* Hidden input for form submission */}
              {name && (
                <HiddenInput
                  type="hidden"
                  name={name}
                  value={value || ''}
                  required={required}
                />
              )}
              <SelectWrapper>
                <Listbox.Button
                  as={Button}
                  $size={size}
                  $variant={variant}
                  $error={error}
                  $isOpen={open}
                  $hasValue={!!selectedOption}
                  $focusColor={focusColor}
                  data-size={size}
                  data-variant={variant}
                  data-error={error}
                  data-disabled={disabled}
                  data-open={open}
                  aria-label={ariaLabel || label}
                  aria-labelledby={ariaLabelledby}
                  aria-describedby={ariaDescribedby}
                  aria-required={required}
                >
                  <ButtonContent>
                    {selectedOption?.icon && (
                      <Icon aria-hidden="true">
                        {selectedOption.icon}
                      </Icon>
                    )}
                    <ButtonText $fontSize={selectedOption ? resolvedSelectedFontSize : resolvedOptionFontSize}>
                      {selectedOption?.label || placeholder}
                    </ButtonText>
                  </ButtonContent>
                  <ButtonActions>
                    {isLoading && (
                      <LoadingSpinner aria-hidden="true">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 1.5V4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M8 12V14.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            opacity="0.5"
                          />
                          <path
                            d="M3.28769 3.28769L5.05025 5.05025"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            opacity="0.875"
                          />
                          <path
                            d="M10.9497 10.9497L12.7123 12.7123"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            opacity="0.375"
                          />
                          <path
                            d="M1.5 8H4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            opacity="0.75"
                          />
                          <path
                            d="M12 8H14.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            opacity="0.25"
                          />
                          <path
                            d="M3.28769 12.7123L5.05025 10.9497"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            opacity="0.625"
                          />
                          <path
                            d="M10.9497 5.05025L12.7123 3.28769"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            opacity="0.125"
                          />
                        </svg>
                      </LoadingSpinner>
                    )}
                    {isClearEnabled && selectedOption && !disabled && !isLoading && (
                      <ClearButton
                        type="button"
                        onClick={handleClear}
                        aria-label="Clear selection"
                        tabIndex={-1}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 4L4 12M4 4L12 12"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </ClearButton>
                    )}
                    <Chevron $isOpen={open} aria-hidden="true">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Chevron>
                  </ButtonActions>
                </Listbox.Button>

                <Transition
                  as={Fragment}
                  leave="leave"
                  leaveFrom="leave-from"
                  leaveTo="leave-to"
                >
                  <Listbox.Options as={Options} $menuPlacement={menuPlacement}>
                    {isSearchEnabled && (
                      <SearchWrapper>
                        <SearchInput
                          type="text"
                          placeholder={searchPlaceholder}
                          value={query}
                          onChange={handleQueryChange}
                          onClick={(e) => e.stopPropagation()}
                          $fontSize={defaultFontSize}
                        />
                      </SearchWrapper>
                    )}
                    <OptionsList>
                      {filteredOptions.length === 0 ? (
                        <NoResults $fontSize={defaultFontSize}>{noResultsMessage}</NoResults>
                      ) : (
                        filteredOptions.map((option: SelectOption) => (
                          <Listbox.Option
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                            as={Fragment}
                          >
                            {({ active, selected }) => (
                              <OptionItem $active={active} $selected={selected} $fontSize={resolvedOptionFontSize}>
                                <OptionContent>
                                  {option.icon && (
                                    <Icon aria-hidden="true">
                                      {option.icon}
                                    </Icon>
                                  )}
                                  <OptionLabel>
                                    {option.label}
                                  </OptionLabel>
                                </OptionContent>
                                {selected && (
                                  <Checkmark aria-hidden="true">
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 20 20"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M16.25 5.625L7.5 14.375L3.75 10.625"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </Checkmark>
                                )}
                              </OptionItem>
                            )}
                          </Listbox.Option>
                        ))
                      )}
                    </OptionsList>
                  </Listbox.Options>
                </Transition>
              </SelectWrapper>
            </>
          );
        }}
      </Listbox>
      {error && errorMessage ? (
        <Message variant="error" size="sm" dataTestId={testId ? `${testId}-error` : undefined}>
          {errorMessage}
        </Message>
      ) : assistiveText ? (
        <Message variant="assistive" size="sm" dataTestId={testId ? `${testId}-helper` : undefined}>
          {assistiveText}
        </Message>
      ) : null}
    </Container>
  );
});

Select.displayName = 'Select';

export default Select;
